import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { CheckCircle2, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import {
  useApplyBoostMutation,
  useStartBoostCheckoutMutation,
  boostApi,
} from '../../ducks/boostApi';
import { selectBoostCredits, selectBoostPrices, setActiveBoost } from '../../ducks/boost.duck';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

import { getStripePromise } from '../../util/stripeLoader';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../ui/utils';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

type BoostListingDialogProps = {
  listingId: string;
  listingTitle: string;
  children: React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
};

type BoostPrice = {
  priceId: string;
  duration: string;
  amountCents: number;
  currency: string;
};

/** A single selectable row. Either a paid Stripe Price or the member's
 * prepaid boost credit (synthetic: no priceId, source='pro_credit'). */
type BoostOption = {
  id: string;
  duration: string;
  amountCents: number;
  currency: string;
  source: 'paid' | 'pro_credit';
  priceId?: string;
};

type Step = 'select' | 'pay' | 'applying' | 'success';

const formatMoney = (amountCents: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: 0,
  }).format(amountCents / 100);

const durationLabelId = (iso: string) => `BoostListingDialog.duration.${iso}`;

const stripePromise = getStripePromise(STRIPE_PUBLISHABLE_KEY);

/** Embedded Stripe Checkout for the boost payment. Uses a Checkout Session so
 * sales tax is applied automatically, in `redirect_on_completion: 'never'` mode
 * so the buyer stays in the dialog. Stripe's UI renders its own pay button and
 * the tax-inclusive total; `onComplete` fires on success and the parent then
 * reconciles the boost server-side. */
const PaymentStep = ({
  clientSecret,
  onConfirmed,
  onBack,
}: {
  clientSecret: string;
  onConfirmed: () => void;
  onBack: () => void;
}) => {
  // Stripe rejects a changed `onComplete`, so the options object and the
  // handler it carries have to keep their identity across renders.
  const onConfirmedRef = React.useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;
  const checkoutOptions = useMemo(
    () => ({ clientSecret, onComplete: () => onConfirmedRef.current() }),
    [clientSecret]
  );

  if (!stripePromise) {
    return (
      <div className="space-y-3 px-5 py-4">
        <div
          className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="text-boost-pay-error"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Stripe is not configured (REACT_APP_STRIPE_PUBLISHABLE_KEY missing)</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="px-5 py-4"
        data-testid="stripe-embedded-checkout"
      >
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={checkoutOptions}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
      <div className="flex items-center justify-start gap-3 border-t border-[#ece6de] bg-[#faf8f4] px-5 py-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-1 text-[12px]"
          data-testid="button-boost-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <FormattedMessage id="BoostListingDialog.back" />
        </Button>
      </div>
    </>
  );
};

const BoostListingDialog = ({
  listingId,
  listingTitle,
  children,
}: BoostListingDialogProps) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const isAuthenticated = useSelector((state: any) => state.auth?.isAuthenticated);
  const dispatch = useDispatch<any>();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('select');
  const applyingRef = React.useRef(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [appliedBoost, setAppliedBoost] = useState<{
    id: string;
    duration: string;
    expiresAt: string;
  } | null>(null);

  const creditsRow = useSelector(selectBoostCredits);
  const cachedPrices = useSelector(selectBoostPrices);
  const [applyBoost, applyState] = useApplyBoostMutation();
  const [startCheckout, checkoutState] = useStartBoostCheckoutMutation();
  const continuing = applyState.isLoading || checkoutState.isLoading;
  const fetchError = null;

  const credits = creditsRow?.remaining ?? 0;
  const prices: BoostPrice[] = useMemo(() => cachedPrices || [], [cachedPrices]);
  const inProgress = prices.length === 0 && !creditsRow;

  const options: BoostOption[] = useMemo(() => {
    const list: BoostOption[] = [];
    const pt24 = prices.find(p => p.duration === 'PT24H');
    if (credits > 0 && pt24) {
      list.push({
        id: 'pro_credit:PT24H',
        duration: 'PT24H',
        amountCents: 0,
        currency: pt24.currency,
        source: 'pro_credit',
      });
    }
    prices.forEach(p => {
      list.push({
        id: `paid:${p.priceId}`,
        duration: p.duration,
        amountCents: p.amountCents,
        currency: p.currency,
        source: 'paid',
        priceId: p.priceId,
      });
    });
    return list;
  }, [prices, credits]);

  const recommendedPaidIndex = useMemo(() => {
    const paidOptions = options.filter(o => o.source === 'paid');
    if (paidOptions.length < 3) return -1;
    const middle = paidOptions[Math.floor(paidOptions.length / 2)];
    return options.findIndex(o => o.id === middle.id);
  }, [options]);

  useEffect(() => {
    if (!selectedId && options.length > 0) {
      const savedIdx = options.findIndex(o => o.source === 'pro_credit');
      const idx = savedIdx >= 0 ? savedIdx : recommendedPaidIndex >= 0 ? recommendedPaidIndex : 0;
      setSelectedId(options[idx].id);
    }
  }, [options, recommendedPaidIndex, selectedId]);

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setStep('select');
      setClientSecret(null);
      setSessionId(null);
      setServerError(null);
      setAppliedBoost(null);
      applyingRef.current = false;
    }
  }, [open]);

  // Flip the boost into active state only when the user dismisses the success
  // panel, so applying it doesn't unmount this dialog mid-confirmation.
  const commitAndClose = () => {
    if (appliedBoost) {
      dispatch(setActiveBoost({ listingId, activeBoost: appliedBoost }));
      dispatch(boostApi.endpoints.fetchBoosts.initiate({ page: 1, limit: 50 }));
    }
    setOpen(false);
  };

  const selected = options.find(o => o.id === selectedId) || null;

  const tryTranslate = (id: string, fallback: string) =>
    intl.formatMessage({ id, defaultMessage: fallback }) || fallback;

  const handleContinue = async () => {
    if (!selected) return;
    setServerError(null);
    if (selected.source === 'pro_credit') {
      try {
        const res = await applyBoost({
          listingId,
          duration: selected.duration,
          source: 'pro_credit',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }).unwrap();
        if (res?.boost) {
          setAppliedBoost({
            id: res.boost.id,
            duration: res.boost.duration,
            expiresAt: res.boost.expiresAt,
          });
        }
        setStep('success');
      } catch (e: any) {
        setServerError(e?.data?.message || 'Could not apply boost');
      }
      return;
    }
    try {
      const { clientSecret: cs, sessionId: sid } = await startCheckout({
        listingId,
        duration: selected.duration,
      }).unwrap();
      setClientSecret(cs);
      setSessionId(sid);
      setStep('pay');
    } catch (e: any) {
      setServerError(e?.data?.message || 'Could not start checkout');
    }
  };

  const handlePayConfirmed = async () => {
    if (!selected) return;
    if (applyingRef.current) return;
    applyingRef.current = true;
    setServerError(null);
    // Swap off the pay step immediately so <EmbeddedCheckout> unmounts (destroys
    // the Stripe instance) and its default success screen never shows.
    setStep('applying');
    try {
      const res = await applyBoost({
        listingId,
        duration: selected.duration,
        source: 'paid',
        sessionId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).unwrap();
      if (res?.boost) {
        setAppliedBoost({
          id: res.boost.id,
          duration: res.boost.duration,
          expiresAt: res.boost.expiresAt,
        });
      }
      setStep('success');
    } catch (e: any) {
      applyingRef.current = false;
      setServerError(e?.data?.message || 'Payment succeeded but the boost did not apply. Contact support.');
    }
  };

  const trigger = React.cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      children.props.onClick?.(e);
      if (!isAuthenticated) {
        const from = `${location.pathname}${location.search}${location.hash}`;
        history.push(createResourceLocatorString('SignupPage', routes, {}, {}), { from });
        return;
      }
      setOpen(true);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger}
      <DialogContent
        className="max-w-[460px] max-h-[90vh] overflow-y-auto border-[#ddd6cd] bg-white p-0 shadow-[0_24px_70px_-42px_rgba(23,21,19,0.35)]"
        data-testid={`dialog-boost-listing-${listingId}`}
        onInteractOutside={e => e.preventDefault()}
      >
        <div className="border-b border-[#ece6de] px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#7e7468]">
            <FormattedMessage id="BoostListingDialog.eyebrow" />
          </p>
          <DialogTitle
            className="mt-1 text-lg font-heading font-bold uppercase leading-tight text-[#171513]"
            data-testid="text-boost-dialog-title"
          >
            {step === 'success' ? (
              <FormattedMessage id="BoostListingDialog.successTitle" />
            ) : step === 'pay' ? (
              <FormattedMessage id="BoostListingDialog.payTitle" />
            ) : (
              <FormattedMessage id="BoostListingDialog.title" />
            )}
          </DialogTitle>
          <DialogDescription
            className="mt-1 text-[13px] leading-5 text-[#5f584e]"
            data-testid="text-boost-dialog-description"
          >
            {step === 'success' ? (
              <FormattedMessage id="BoostListingDialog.successDescription" />
            ) : step === 'pay' && selected ? (
              <FormattedMessage
                id="BoostListingDialog.payDescription"
                values={{
                  duration: tryTranslate(durationLabelId(selected.duration), selected.duration),
                  amount: formatMoney(selected.amountCents, selected.currency),
                }}
              />
            ) : (
              <FormattedMessage id="BoostListingDialog.description" />
            )}
          </DialogDescription>
        </div>

        {step === 'success' ? (
          <>
            <div className="px-5 py-6 text-center" data-testid="panel-boost-success">
              <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
              <p className="mt-3 text-sm text-[#171513]">
                <FormattedMessage
                  id="BoostListingDialog.successBody"
                  values={{
                    listingTitle,
                    duration: selected
                      ? tryTranslate(durationLabelId(selected.duration), selected.duration)
                      : '',
                    expiry: appliedBoost
                      ? new Intl.DateTimeFormat(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }).format(new Date(appliedBoost.expiresAt))
                      : '',
                  }}
                />
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#ece6de] bg-[#faf8f4] px-5 py-3">
              <Button
                type="button"
                onClick={commitAndClose}
                size="sm"
                className="font-heading uppercase tracking-[0.12em]"
                data-testid="button-boost-done"
              >
                <FormattedMessage id="BoostListingDialog.done" />
              </Button>
            </div>
          </>
        ) : step === 'applying' ? (
          serverError ? (
            <div className="px-5 py-6 text-center" data-testid="panel-boost-apply-failed">
              <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
              <p className="mt-3 text-sm text-[#171513]">{serverError}</p>
              <div className="mt-4 flex items-center justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                  className="font-heading uppercase tracking-[0.12em]"
                  data-testid="button-boost-apply-close"
                >
                  <FormattedMessage id="BoostListingDialog.close" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handlePayConfirmed}
                  className="font-heading uppercase tracking-[0.12em]"
                  data-testid="button-boost-apply-retry"
                >
                  <FormattedMessage id="BoostListingDialog.retry" />
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center gap-3 px-5 py-8"
              data-testid="panel-boost-applying"
            >
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                <FormattedMessage id="BoostListingDialog.applying" />
              </p>
            </div>
          )
        ) : step === 'pay' && clientSecret && selected ? (
          <PaymentStep
            clientSecret={clientSecret}
            onConfirmed={handlePayConfirmed}
            onBack={() => {
              setStep('select');
              setClientSecret(null);
              setServerError(null);
            }}
          />
        ) : (
          <>
            <div className="px-5 py-3">
              {inProgress && options.length === 0 ? (
                <p className="py-3 text-center text-sm text-[#5f584e]">
                  <FormattedMessage id="BoostListingDialog.loading" />
                </p>
              ) : fetchError ? (
                <p className="py-3 text-center text-sm text-destructive">
                  <FormattedMessage id="BoostListingDialog.errorLoading" />
                </p>
              ) : options.length === 0 ? (
                <p className="py-3 text-center text-sm text-[#5f584e]">
                  <FormattedMessage id="BoostListingDialog.noPlansConfigured" />
                </p>
              ) : (
                <div className="space-y-2">
                  {options.map((option, index) => {
                    const isSelected = option.id === selectedId;
                    const isPrepaid = option.source === 'pro_credit';
                    const recommended = index === recommendedPaidIndex && !isPrepaid;
                    const durationLabel = tryTranslate(durationLabelId(option.duration), option.duration);

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedId(option.id)}
                        aria-pressed={isSelected}
                        disabled={continuing}
                        className={cn(
                          'flex w-full items-center justify-between gap-3 rounded-md border px-4 py-3 text-left transition-colors disabled:opacity-60',
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-[#e5dfd7] bg-white hover:border-primary/40'
                        )}
                        data-testid={`button-boost-option-${option.source}-${option.duration}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              'flex h-4 w-4 items-center justify-center rounded-full border',
                              isSelected ? 'border-primary bg-primary' : 'border-[#cbc3b8] bg-white'
                            )}
                            aria-hidden="true"
                          >
                            {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                          </span>
                          <span className="text-sm font-heading font-bold uppercase text-[#171513]">
                            {isPrepaid ? (
                              <FormattedMessage
                                id="BoostListingDialog.prepaidBoostLabel"
                                values={{ count: credits }}
                              />
                            ) : (
                              durationLabel
                            )}
                          </span>
                          {isPrepaid && (
                            <Badge className="rounded-md bg-primary px-1.5 py-0 text-[9px] uppercase tracking-[0.14em] text-primary-foreground">
                              <FormattedMessage id="BoostListingDialog.includedBadge" />
                            </Badge>
                          )}
                          {recommended && (
                            <Badge className="rounded-md bg-[#171513] px-1.5 py-0 text-[9px] uppercase tracking-[0.14em] text-white">
                              <FormattedMessage id="BoostListingDialog.bestValue" />
                            </Badge>
                          )}
                        </div>
                        <span
                          className="text-sm font-semibold text-[#171513]"
                          data-testid={`text-boost-option-price-${option.source}-${option.duration}`}
                        >
                          {isPrepaid ? (
                            <FormattedMessage id="BoostListingDialog.free" />
                          ) : (
                            formatMoney(option.amountCents, option.currency)
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {serverError && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  data-testid="text-boost-server-error"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{serverError}</span>
                </div>
              )}
            </div>

            <div
              className="flex items-center justify-between gap-3 border-t border-[#ece6de] bg-[#faf8f4] px-5 py-3"
              data-testid="panel-boost-checkout-note"
            >
              <div className="min-w-0 text-[12px] text-[#5f584e]">
                {selected ? (
                  <span data-testid="text-boost-selected-summary">
                    <FormattedMessage
                      id={
                        selected.source === 'pro_credit'
                          ? 'BoostListingDialog.summaryPrepaid'
                          : 'BoostListingDialog.summaryPaid'
                      }
                      values={{
                        duration: tryTranslate(durationLabelId(selected.duration), selected.duration),
                        amount: formatMoney(selected.amountCents, selected.currency),
                      }}
                    />
                  </span>
                ) : (
                  <FormattedMessage id="BoostListingDialog.summaryEmpty" />
                )}
              </div>
              <Button
                type="button"
                onClick={handleContinue}
                disabled={!selected || continuing}
                size="sm"
                className="font-heading uppercase tracking-[0.12em]"
                data-testid="button-boost-continue"
              >
                {continuing ? (
                  <FormattedMessage id="BoostListingDialog.processing" />
                ) : (
                  <FormattedMessage
                    id={
                      selected?.source === 'pro_credit'
                        ? 'BoostListingDialog.applyFree'
                        : 'BoostListingDialog.continue'
                    }
                  />
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BoostListingDialog;
